"use client";

import { useMantineTheme } from "@mantine/core";
import * as Sentry from "@sentry/nextjs";
import Gleap from "gleap";
import { useLocale } from "next-intl";
import { NavigationGuardProvider } from "next-navigation-guard";
import NextTopLoader from "nextjs-toploader";
import { memo, type ReactNode, useEffect } from "react";
import { env, isDevelopment } from "@/env";
import { createClient } from "@/lib/supabase";
import { PostHogProvider } from "@/providers/PostHogProvider";

// Gleap Widget
// This component is separated from ClientLayout because it needs to be wrapped with <SessionProvider> to use useSession() hook
const GleapWidget = memo(() => {
  const locale = useLocale();

  useEffect(() => {
    if (env.NEXT_PUBLIC_GLEAP_API_KEY) {
      Gleap.setLanguage(locale);
      Gleap.initialize(env.NEXT_PUBLIC_GLEAP_API_KEY);
      Gleap.setMaxNetworkRequests(50);

      if (isDevelopment()) {
        console.log("Setting Gleap environment to development");
        Gleap.setEnvironment("dev");
      }
    }

    return () => {
      Gleap.destroy();
    };
  }, [locale]);

  return null;
});

// All the client wrappers are here (they can't be in server components)
const LayoutClient = ({ children }: { children: ReactNode }) => {
  const theme = useMantineTheme();

  // Enrich Sentry events with user data
  useEffect(() => {
    const supabase = createClient();
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        Sentry.setUser({
          id: session.user.id,
          email: session.user.email,
        });
      } else {
        Sentry.setUser(null);
      }
    });

    return () => {
      data.subscription.unsubscribe();
    };
  }, []);

  // Check for outdated browser
  useEffect(() => {
    // Only run on client side
    if (typeof window !== "undefined") {
      console.debug("Browser Update Init", window.navigator.userAgent);
      import("browser-update").then((bu) => {
        console.debug("browser-update check");
        bu.default({
          required: {
            e: -4,
            f: -3,
            o: -3,
            s: -1,
            c: -3,
          },
          insecure: true,
          unsupported: true,
          style: "bottom",
          reminder: 0,
          reminderClosed: 1,
          onshow: (info) => {
            console.warn("Browser Update Show", info);
          },
          api: 2025.04,
        });
      });
    }
  }, []);

  return (
    <PostHogProvider>
      {/* Show a progress bar at the top when navigating between pages */}
      <NextTopLoader color={theme.primaryColor} showSpinner={false} />

      {/* Set Gleap widget */}
      <GleapWidget />

      <NavigationGuardProvider>{children}</NavigationGuardProvider>
    </PostHogProvider>
  );
};

export default LayoutClient;
